/**
 * `helpCorpus` — parse the raw help-doc corpus (from `virtual:help-corpus`,
 * backed by the repo-root `docs/help/` source of truth) into the model the
 * Help Center renders: per-doc `{ slug, title, body }` and an ordered nav tree.
 *
 * PARSING lives here (not in the Vite plugin) so it is unit-testable against
 * the real content under Vitest. See `tools/help-corpus-plugin.ts` for the
 * build-mechanism rationale.
 *
 * - Frontmatter `title:` drives the page title and every nav/link label
 *   (requirement §3).
 * - Nav ORDER + GROUPING come from `docs/help/site/_nav.md` when it is usable;
 *   otherwise a sensible order is derived (getting-started first) and
 *   `helpNavDerived` is set so callers/tests can tell.
 */
import {
  helpSources,
  navSource,
  helpAssets,
  helpPdfLabels,
} from "virtual:help-corpus";

import { HELP_INDEX_SLUG } from "@/help/resolveHelpLink";

export interface HelpDoc {
  slug: string;
  title: string;
  /** Markdown body with the frontmatter block stripped. */
  body: string;
}

export interface HelpNavItem {
  slug: string;
  title: string;
  children: HelpNavItem[];
}

const FRONTMATTER_RE = /^﻿?---\r?\n([\s\S]*?)\r?\n---\r?\n?/;
const TITLE_RE = /^title:\s*(.+?)\s*$/m;

function parseFrontmatterTitle(raw: string): { title: string | null; body: string } {
  const fm = FRONTMATTER_RE.exec(raw);
  if (!fm) return { title: null, body: raw };
  const block = fm[1] ?? "";
  const titleMatch = TITLE_RE.exec(block);
  const title = titleMatch?.[1]?.trim() ?? null;
  const body = raw.slice(fm[0].length);
  return { title, body };
}

/** Humanize a slug as a last-resort label ("getting-started" → "Getting Started"). */
function humanizeSlug(slug: string): string {
  return slug
    .split("-")
    .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function buildDocs(): Map<string, HelpDoc> {
  const docs = new Map<string, HelpDoc>();
  for (const [slug, raw] of Object.entries(helpSources)) {
    const { title, body } = parseFrontmatterTitle(raw);
    docs.set(slug, { slug, title: title ?? humanizeSlug(slug), body });
  }
  return docs;
}

export const helpDocs: Map<string, HelpDoc> = buildDocs();

/** All slugs that exist in the corpus (including the `index` landing). */
export const helpKnownSlugs: ReadonlySet<string> = new Set(helpDocs.keys());

export function getHelpDoc(slug: string): HelpDoc | undefined {
  return helpDocs.get(slug);
}

// ── Nav parsing ─────────────────────────────────────────────────────────────

const NAV_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/;

/** Resolve a `_nav.md` link target to a corpus slug, or null if not published. */
function navTargetToSlug(href: string): string | null {
  const path = href.split("#")[0] ?? "";
  if (!/\.md$/i.test(path)) return null;
  const last = path.split("/").pop() ?? "";
  const slug = last.replace(/\.md$/i, "");
  return slug.length > 0 ? slug : null;
}

function labelFor(slug: string): string {
  return helpDocs.get(slug)?.title ?? humanizeSlug(slug);
}

/**
 * Parse `_nav.md` into an ordered nav tree.
 *
 * The manifest is a numbered top-level list; indented `-` bullets attach to the
 * preceding top-level item as children (the Integrations → Greenhouse/Teams
 * grouping). The trailing "Home page:" line (the `index` landing) is skipped —
 * the landing is reached via the Help nav entry point, not a nav row.
 */
function parseNav(source: string): HelpNavItem[] {
  const items: HelpNavItem[] = [];
  let current: HelpNavItem | null = null;

  for (const rawLine of source.split(/\r?\n/)) {
    const link = NAV_LINK_RE.exec(rawLine);
    if (!link) continue;
    // Skip the "Home page: [index.md](index.md)" line.
    if (/^\s*home page:/i.test(rawLine)) continue;

    const href = link[2] ?? "";
    const slug = navTargetToSlug(href);
    if (!slug || slug === HELP_INDEX_SLUG || !helpDocs.has(slug)) continue;

    const indented = /^\s+[-*]/.test(rawLine);
    const node: HelpNavItem = { slug, title: labelFor(slug), children: [] };

    if (indented && current) {
      current.children.push(node);
    } else {
      items.push(node);
      current = node;
    }
  }

  return items;
}

/**
 * Fallback IA when `_nav.md` is empty/unusable: getting-started first, then the
 * remaining docs in source order (the per-vendor integration guides last).
 */
function deriveNav(): HelpNavItem[] {
  const guides = new Set([
    "greenhouse-integration",
    "ashby-integration",
    "lever-integration",
    "jobdiva-integration",
    "teams-integration",
  ]);
  const order: string[] = [];
  if (helpDocs.has("getting-started")) order.push("getting-started");
  for (const slug of helpDocs.keys()) {
    if (slug === HELP_INDEX_SLUG || slug === "getting-started") continue;
    if (guides.has(slug)) continue;
    order.push(slug);
  }
  for (const slug of guides) if (helpDocs.has(slug)) order.push(slug);
  return order.map((slug) => ({ slug, title: labelFor(slug), children: [] }));
}

const parsedNav = parseNav(navSource);
export const helpNavDerived = parsedNav.length === 0;
export const helpNav: HelpNavItem[] = helpNavDerived ? deriveNav() : parsedNav;

/** Flat, ordered list of nav slugs (top-level + children) for prev/next + tests. */
export const helpNavFlat: HelpNavItem[] = helpNav.flatMap((item) => [
  item,
  ...item.children,
]);

export { helpAssets, helpPdfLabels };
